import React, { useContext, useState, useRef, Fragment, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import { Dialog, Transition } from "@headlessui/react";
import { Spinner } from "../../Spinner";
import { generateInventoryReport } from "../../service/Inventory";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import datePickerPopper from "../common/DatePickerPopper";
import useDirection from "../../hooks/useDirection";

const parseYmd = (s) => {
  if (!s) return null;
  const p = String(s).split("-").map(Number);
  if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
  return new Date(p[0], p[1] - 1, p[2]);
};
const formatYmd = (d) => {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const DownloadReport = ({ openmodal, setOpenModal }) => {
  const { theme, selectedOrgId, langMode } = useContext(AppContext);
  const { isRTL: isRtl, dir } = useDirection();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const cancelButtonRef = useRef(null);
  const todayPicker = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const navigateToBack = () => {
    setOpenModal(false);
  };

  const reportDownloadInventory = async () => {
    try {
      setLoading(true);
      const data = await generateInventoryReport(
        selectedOrgId,
        startDate,
        endDate
      );

      if (data.header.code === 600) {
        const s3Link = data.body.value;
        toast.success(translations[langMode].reportgeneratedsuccc);
        const link = document.createElement("a");
        link.href = s3Link;
        link.setAttribute("download", "inventory_report.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        navigateToBack();
      } else if (data.header.code === 607) {
        toast.error(data.body.value);
      }
    } catch (error) {
      console.error("Error while generating report:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition.Root show={openmodal} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[100]"
        initialFocus={cancelButtonRef}
        onClose={() => setOpenModal(false)}
        dir={dir}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity `}
          />
        </Transition.Child>
        <div className="fixed inset-0 z-[100] w-screen overflow-y-auto">
          <div className="flex min-h-full justify-center p-2.5 sm:p-4 text-center items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative transform overflow-hidden rounded-xl sm:rounded-2xl bg-white shadow-xl transition-all sm:my-8 w-full max-w-lg max-h-[88vh] flex flex-col ${
                  theme === "dark"
                    ? "dark-mode"
                    : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                }`}
              >
                {loading && <Spinner />}
                <div className="shrink-0 flex justify-between items-center px-4 sm:px-5 py-3.5 sm:py-4 border-b border-slate-100">
                  <h4
                    className={`text-base font-semibold
          ${theme === "dark" ? "text-white" : "text-gray-800"}
          ${theme === "high-contrast" ? "text-black" : "text-gray-700"}`}
                  >
                    {translations[langMode].downloadsheet}
                  </h4>
                  <ModalCloseButton onClick={navigateToBack} />
                </div>
                <div
                  className={`flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 flex flex-col gap-4 ${
                    theme === "dark"
                      ? "dark-mode"
                      : theme === "high-contrast"
                      ? "high-contrast"
                      : ""
                  }`}
                >
                  <div className="flex w-full min-w-0 items-center gap-2.5 sm:gap-4">
                    <div className="flex-1 min-w-0 flex flex-col gap-y-1.5">
                      <label className="text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300">{translations[langMode].fromdate}</label>
                      <DatePicker
                        selected={parseYmd(startDate)}
                        onChange={(date) => {
                          setStartDate(date ? formatYmd(date) : "");
                          if (date && parseYmd(endDate) && parseYmd(endDate) < date) {
                            setEndDate(formatYmd(date));
                          }
                        }}
                        placeholderText={translations[langMode].fromdate}
                        maxDate={todayPicker}
                        selectsStart
                        startDate={parseYmd(startDate)}
                        endDate={parseYmd(endDate)}
                        className={`w-full h-10 px-2.5 !rounded-lg text-xs sm:text-sm border border-gray-300 ${
                          theme === "dark"
                            ? "dark-mode border-gray-200 placeholder-white"
                            : theme === "high-contrast"
                            ? "high-contrast"
                            : ""
                        }`}
                        calendarClassName="!rounded-lg shadow-lg"
                        popperPlacement="bottom-start"
                        popperContainer={datePickerPopper}
                      />
                    </div>
                    <span className={`text-slate-400 text-sm hidden sm:inline self-end mb-2 ${isRtl ? "rotate-180" : ""}`}>→</span>
                    <div className="flex-1 min-w-0 flex flex-col gap-y-1.5">
                      <label className="text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300">{translations[langMode].todate}</label>
                      <DatePicker
                        selected={parseYmd(endDate)}
                        onChange={(date) => setEndDate(date ? formatYmd(date) : "")}
                        placeholderText={translations[langMode].todate}
                        minDate={parseYmd(startDate)}
                        maxDate={todayPicker}
                        selectsEnd
                        startDate={parseYmd(startDate)}
                        endDate={parseYmd(endDate)}
                        disabled={!startDate}
                        className={`w-full h-10 px-2.5 !rounded-lg text-xs sm:text-sm border border-gray-300 ${
                          theme === "dark"
                            ? "dark-mode border-gray-200 placeholder-white"
                            : theme === "high-contrast"
                            ? "high-contrast"
                            : ""
                        }`}
                        calendarClassName="!rounded-lg shadow-lg"
                        popperPlacement="bottom-start"
                        popperContainer={datePickerPopper}
                      />
                    </div>
                  </div>
                </div>
                <div
                  className={`shrink-0 sticky bottom-0 bg-white flex w-full justify-end items-center gap-2 sm:gap-3 border-t px-4 sm:px-5 py-3 sm:py-4 ${
                    theme === "dark"
                      ? "dark-mode"
                      : theme === "high-contrast"
                      ? "high-contrast"
                      : ""
                  }`}
                >
                  <CommonButton colorIntent="danger"
                    onClick={navigateToBack}
                    variant="danger"
                    theme={theme}
                    wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                    className="w-full !h-10 !rounded-lg justify-center"
                  >
                    {translations[langMode].cancel}
                  </CommonButton>
                  <CommonButton colorIntent="primary"
                    onClick={reportDownloadInventory}
                    theme={theme}
                    variant="primary"
                    tooltipText={translations[langMode].downloadthereportfortheselecte}
                    tooltipPosition="top"
                    wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                    className="w-full !h-10 !rounded-lg justify-center"
                  >
                    {translations[langMode].download}
                  </CommonButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
